// TopBar Team dropdown - 1-click team access from every page.
//
// Trigger sits in the TopBar between NotificationsBell and ConnectionsPill.
// Pops a 280px panel anchored below the trigger listing the real staff from
// /api/firm/staff - fetched only once the panel opens, never the prototype
// team. Rows navigate to /team/:userId via EntityLink kind="team".
//
// Closes on: outside click, Escape, route change.
//
// Mobile (<=768px): trigger collapses to icon-only via CSS in
// `team-dropdown.css` so it doesn't crowd the TopBar alongside the
// NotificationsBell + cmd-K + sign-in chip.

import { Link } from "react-router-dom";
import { Users, ChevronDown, UserPlus } from "lucide-react";
import { useDismissable } from "../../hooks/useDismissable";
import { useFirmStaff } from "../../hooks/useFirmStaff";
import { colorFor } from "../../lib/staffVisuals";
import { initialsOf } from "../../api/auth";
import { EntityLink } from "./EntityLink";
import { ARROW } from "./SepList";

export function TeamDropdown() {
  const { open, setOpen, containerRef } = useDismissable<HTMLDivElement>();
  const { data: staff, isLoading, isError } = useFirmStaff({ enabled: open });

  return (
    <div ref={containerRef} className="cc-team-dd" style={{ position: "relative" }}>
      <button
        type="button"
        className="cc-team-dd-btn"
        aria-label="Team"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Users size={14} strokeWidth={2} aria-hidden />
        <span className="cc-team-dd-label">Team</span>
        <ChevronDown
          size={12}
          strokeWidth={2}
          aria-hidden
          className={`cc-team-dd-chev${open ? " open" : ""}`}
        />
      </button>

      {open && (
        <div className="cc-team-dd-panel cc-card" role="menu" aria-label="Firm team">
          <div className="cc-team-dd-head">
            <span className="cc-team-dd-head-count">
              {staff ? `${staff.length} team ${staff.length === 1 ? "member" : "members"}` : "Team"}
            </span>
            <Link to="/team" className="cc-team-dd-head-link">
              Manage {ARROW}
            </Link>
          </div>

          <div className="cc-team-dd-list">
            {isLoading && (
              <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--muted)" }}>
                Loading team...
              </div>
            )}
            {isError && (
              <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--warn-600)" }}>
                Could not reach the server.
              </div>
            )}
            {staff && staff.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--muted)" }}>
                No staff accounts yet.
              </div>
            )}
            {staff?.map((m) => (
              <EntityLink
                key={m.id}
                kind="team"
                id={m.id}
                className="cc-team-dd-row cc-row-lift"
                ariaLabel={`Open ${m.fullName} profile`}
              >
                <span className="av" style={{ background: colorFor(m.id) }} aria-hidden>
                  {initialsOf(m.fullName)}
                </span>
                <span className="cc-team-dd-row-text">
                  <span className="name">{m.fullName}</span>
                  <span className="role">{m.role}</span>
                </span>
              </EntityLink>
            ))}
          </div>

          <div className="cc-team-dd-foot">
            <button
              type="button"
              className="cc-team-dd-add"
              disabled
              aria-disabled="true"
              title="Coming soon"
            >
              <UserPlus size={12} strokeWidth={2} aria-hidden />
              <span>Add team member</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
